import { useTranslation } from "@repo/i18n";
import type { VenueConfig } from "../config/types";
import { resolveBookingConfig } from "../config/types";

/**
 * Fail-safe shell states (features/booking-widget-embed.md R4). Each is a plain, self-contained frame
 * — never a blank block, never a raw error — that renders identically in all three mount modes and at
 * ≥320px. The unavailable frame is the RENDERING half of the `OnlineBookingDisabled`/`VenueNotLive`
 * rejections (DOMAIN.md §C.6); confirm-time enforcement lands with tasks/diner-booking-api.md.
 */

/** Loading placeholder while the public venue config is in flight. */
export function LoadingState() {
  const { t } = useTranslation();
  return (
    <output className="rsv-state" aria-live="polite">
      <p>{t("widget.loading")}</p>
    </output>
  );
}

/**
 * Booking is switched off (online booking disabled) or the venue is not live yet. This is a RESOLVED
 * venue — so the frame is deliberately a contact fallback, not a dead end: the venue name (already in
 * the shell header) plus a clear "online booking unavailable" message and, when the config carries
 * contact details, tap-to-call / email actions so a diner can still reach the venue.
 */
export function UnavailableState({ config }: { config?: VenueConfig | null }) {
  const { t } = useTranslation();
  const contact = config ? resolveBookingConfig(config).contact : {};
  const phone = contact.phone?.trim();
  const email = contact.email?.trim();
  const hasContact = Boolean(phone || email);

  return (
    <div className="rsv-state">
      <p className="rsv-state-icon" aria-hidden="true">
        🍽️
      </p>
      <h2>{t("widget.unavailable.title")}</h2>
      <p>{t("widget.unavailable.body")}</p>
      {hasContact ? (
        <div className="rsv-contact">
          <p className="rsv-contact-lead">{t("widget.unavailable.contactLead")}</p>
          <div className="rsv-contact-actions">
            {phone ? (
              <a className="rsv-button" href={`tel:${phone.replace(/\s+/g, "")}`}>
                {t("widget.unavailable.call")}
              </a>
            ) : null}
            {email ? (
              <a className="rsv-button rsv-button-secondary" href={`mailto:${email}`}>
                {t("widget.unavailable.email")}
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Unknown/malformed slug — the booking link does not resolve to a venue. */
export function NotFoundState() {
  const { t } = useTranslation();
  return (
    <div className="rsv-state">
      <h2>{t("widget.notFound.title")}</h2>
      <p>{t("widget.notFound.body")}</p>
    </div>
  );
}

/** Transient failure (config fetch error) — offers a retry rather than a dead end. */
export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rsv-state" role="alert">
      <h2>{t("widget.error.title")}</h2>
      <p>{t("widget.error.body")}</p>
      {onRetry ? (
        <button type="button" className="rsv-button" onClick={onRetry}>
          {t("widget.retry")}
        </button>
      ) : null}
    </div>
  );
}
